import { Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useRoutes } from 'react-router-dom';
import { useLocalStorage } from '@mantine/hooks';
import * as Sentry from '@sentry/react';
import { QueryErrorResetBoundary } from '@tanstack/react-query';
import { useShallow } from 'zustand/react/shallow';

import '../i18n/config';

import { FullScreenError } from '../components/fullscreenframe/FullScreenError';
import { FullScreenLoader } from '../components/fullscreenframe/FullScreenLoader';
import { TenantPreLoaderChildren } from '../components/tenantpreloader/TenantPreLoader';
import { useSessionLifecycle } from '../hooks/useSessionLifecycle';
import { useVegaFunctions } from '../hooks/useVegaFunctions';
import { TenantPreLoader } from '../scenes/TenantPreLoader';
import { useStore } from '../store';
import { COLOR_SCHEME_STORAGE_KEY } from '../utils/constants';

import { routes } from './routes';
import { usePortalTheme } from './usePortalTheme';

export function App({
  data: preLoaderData,
  isError: isPreLoaderError,
}: TenantPreLoaderChildren) {
  // set timer for token refresh, initSession needs to have been called before.
  useSessionLifecycle();

  const [isLoggedIn, session] = useStore(
    useShallow((state) => [
      state.isSessionAuthenticated() && !state.isSessionExpired(),
      state.session,
    ])
  );

  const theme = preLoaderData?.theme;

  const [colorScheme, setColorScheme] = useLocalStorage<
    'dark' | 'light' | undefined
  >({
    getInitialValueInEffect: false,
    key: COLOR_SCHEME_STORAGE_KEY,
    defaultValue: theme?.activeTheme,
  });

  // always use active theme if user isn't allowed to switch themes
  useEffect(() => {
    if (
      theme &&
      colorScheme !== theme.activeTheme &&
      theme.userCanSwitchTheme === false
    ) {
      setColorScheme(theme.activeTheme);
    }
  }, [colorScheme, setColorScheme, theme]);

  usePortalTheme(colorScheme, theme);
  useVegaFunctions();

  const routing = useRoutes(
    session && preLoaderData
      ? routes(isLoggedIn)
      : [
          {
            path: '*',
            element: <TenantPreLoader isError={isPreLoaderError} />,
          },
        ]
  );

  return (
    <Suspense fallback={<FullScreenLoader />}>
      <QueryErrorResetBoundary>
        {({ reset }) => (
          <ErrorBoundary
            FallbackComponent={FullScreenError}
            onError={(error) => {
              Sentry.captureException(error);
            }}
            onReset={reset}
          >
            {routing}
          </ErrorBoundary>
        )}
      </QueryErrorResetBoundary>
    </Suspense>
  );
}

export default App;
